import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import { debounce, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import {
  Highlight,
  useClearRefinements,
  useHits,
  useRefinementList,
  useSearchBox,
} from 'react-instantsearch-hooks-web';
import { Link } from 'react-router-dom';

import CloseIcon from '../../assets/icons/CloseIcon';
import FilterIcon from '../../assets/icons/FilterIcon';
import Idea from '../../assets/icons/Idea';
import OpenedBook from '../../assets/icons/OpenedBook';
import PathwayIcon from '../../assets/icons/PathwayIcon';
import VideoIcon from '../../assets/icons/VideoIcon';
import { FILTERS, HITS_TYPES } from '../../pages/DiscoverPage/const';
import styled from '../../styled';
import {
  colorsPalette,
  LARGE_MARGIN_PX,
  MIDDLE_RADIUS_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
} from '../../theme';
import { formatDateToNewsFormat, secondsToHms } from '../../utils/helpers';
import highlight from '../../utils/highlight';
import useInsightsMiddleware from '../../utils/hooks/useInsightsMiddleware';
import CardDurationInfo from '../Card/components/CardDurationInfo/CardDurationInfo';

const extractSubcategory = (label) => label?.split('>')[1]?.trim();

const SEARCH_REFINEMENT_CLICKED = 'Search Refinement Clicked';
const SEARCH_HIT_CLICKED = 'Search Hit Clicked';

const MenuItem = ({
  link,
  title,
  subtitle = null,
  icon,
  onClick,
  ...rest
}: {
  link: string;
  title: ReactNode;
  subtitle?: ReactNode;
  icon: ReactNode;
  onClick: () => void;
}) => (
  <ItemWrapper data-testid={rest['data-testid'] + 'Link'} to={link} onClick={onClick}>
    {icon}
    <div>
      {!!title && <ItemTitle data-testid={rest['data-testid'] + 'Title'}>{title}</ItemTitle>}
      {!!subtitle && <span data-testid={rest['data-testid'] + 'Subtitle'}>{subtitle}</span>}
    </div>
  </ItemWrapper>
);

const useStyles = makeStyles(() =>
  createStyles({
    duration: {
      fontSize: 12,
    },
  }),
);

const highlightByMark = highlight((matchQuery) => `<mark>${matchQuery}</mark>`);

const Dropdown = ({
  selectedCategoriesRef,
  value,
  close,
  canCheckAlgoliaToken,
  wrapperStyles = '',
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [selectedRefinement, setSelectedRefinement] = useState(null);
  const { hits, sendEvent, results } = useHits();
  const { refine: refineHits, query } = useSearchBox();
  const {
    items: refinements,
    refine: refineRefinements,
    sendEvent: sendRefinementEvent,
  } = useRefinementList({
    attribute: FILTERS.Subcategory,
    limit: 3,
  });
  const { refine: clearRefs } = useClearRefinements();

  useInsightsMiddleware({
    canCheckToken: canCheckAlgoliaToken,
    allowedInsightMethods: ['clickedObjectIDsAfterSearch', 'clickedObjectIDs', 'clickedFilters'],
    queryID: results?.queryID,
  });

  const filteredRefinements = refinements.filter((ref) => !ref.isRefined);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedRefineHits = useCallback(debounce(refineHits, 500), [refineHits]);

  useEffect(() => {
    debouncedRefineHits(value);
  }, [value, debouncedRefineHits]);

  useEffect(() => {
    // @ts-ignore
    selectedCategoriesRef.current = selectedRefinement?.value?.split('>').map((i) => i.trim());
  }, [selectedRefinement, selectedCategoriesRef]);

  const videos: any[] = hits.filter((hit) => hit.searchable_type === HITS_TYPES.VIDEO);
  const pathways: any[] = hits.filter((hit) => hit.searchable_type === HITS_TYPES.PATHWAY);
  const glossary: any[] = hits.filter((hit) => hit.searchable_type === HITS_TYPES.GLOSSARY);
  const insights: any[] = hits.filter((hit) => hit.searchable_type === HITS_TYPES.NEWS);

  const hasResults = !!videos.length || !!pathways.length || !!glossary.length || !!insights.length;

  if (!hasResults || !query) {
    return null;
  }

  const handleRefinementClick = (ref) => () => {
    const refinementValue = ref.value;
    sendRefinementEvent('click', refinementValue, SEARCH_REFINEMENT_CLICKED);

    if (selectedRefinement) {
      clearRefs();
    }

    refineRefinements(ref.value);
    setSelectedRefinement(ref);
  };

  const handleHitClick = (hit) => () => {
    close();
    sendEvent('click', hit, SEARCH_HIT_CLICKED);
  };

  return (
    <Wrapper data-testid="dropdownMenu" optionalStyles={wrapperStyles}>
      <Content>
        {selectedRefinement && (
          <SelectedCategories>
            <SelectedCategory
              data-testid="selectedFilter"
              onClick={() => {
                clearRefs();
                setSelectedRefinement(null);
              }}
            >
              {
                // @ts-ignore
                extractSubcategory(selectedRefinement?.label)
              }
              <StyledCloseIcon />
            </SelectedCategory>
          </SelectedCategories>
        )}
        {!!filteredRefinements.length && (
          <>
            <SectionTitle>{t('page.discover.categories')}:</SectionTitle>
            <Subsection>
              {filteredRefinements.map((refinement) => (
                <Subcategory
                  data-testid="subcategoryFilter"
                  onClick={handleRefinementClick(refinement)}
                >
                  <Highlight
                    // @ts-ignore
                    hit={{
                      _highlightResult: {
                        name: {
                          // @ts-ignore
                          value: highlightByMark(value, extractSubcategory(refinement.label)),
                        },
                      },
                    }}
                    attribute="name"
                  />
                  <StyledFilterIcon />
                </Subcategory>
              ))}
            </Subsection>
          </>
        )}
        <SectionTitle>{t('page.discover.results')}:</SectionTitle>
        {!!pathways.length && (
          <>
            <SubsectionTitle>{t('page.discover.pathways')}:</SubsectionTitle>
            <Subsection>
              {pathways.map((hit) => (
                <MenuItem
                  data-testid={`pathwayItem`}
                  link={`/pathway/${hit.slug}`}
                  icon={
                    <StyledPathwayIcon
                      color={colorsPalette.violet}
                      secondaryColor={'transparent'}
                    />
                  }
                  key={hit.id}
                  title={<Highlight hit={hit} attribute="name" />}
                  subtitle={
                    <CardDurationInfo
                      className={classes.duration}
                      durationName={`${hit?.count_videos} videos`}
                      duration={secondsToHms(hit?.duration)}
                    />
                  }
                  onClick={handleHitClick(hit)}
                />
              ))}
            </Subsection>
          </>
        )}
        {!!videos.length && (
          <>
            <SubsectionTitle>{t('page.discover.videoModules')}:</SubsectionTitle>
            <Subsection>
              {videos.map((hit) => (
                <MenuItem
                  data-testid={`videoItem`}
                  link={`/videos/${hit.video_url}`}
                  icon={<StyledVideoIcon />}
                  key={hit.id}
                  title={<Highlight hit={hit} attribute="title" />}
                  subtitle={
                    <CardDurationInfo
                      className={classes.duration}
                      durationName={hit?.user}
                      duration={secondsToHms(hit?.duration)}
                    />
                  }
                  onClick={handleHitClick(hit)}
                />
              ))}
            </Subsection>
          </>
        )}
        {!!glossary.length && (
          <>
            <SubsectionTitle>{t('page.discover.glossary')}:</SubsectionTitle>
            <Subsection>
              {glossary.map((hit) => (
                <MenuItem
                  data-testid={`glossaryItem`}
                  link={`/discover/glossary/${hit.slug}`}
                  key={hit.id}
                  title={<Highlight hit={hit} attribute="title" />}
                  icon={<StyledOpenedBook />}
                  onClick={handleHitClick(hit)}
                />
              ))}
            </Subsection>
          </>
        )}
        {!!insights.length && (
          <>
            <SubsectionTitle>{t('page.discover.insights')}:</SubsectionTitle>
            <Subsection>
              {insights.map((hit) => (
                <MenuItem
                  data-testid={`insightItem`}
                  link={`/news/${hit?.id}`}
                  key={hit.id}
                  title={<Highlight hit={hit} attribute="title" />}
                  subtitle={
                    <CardDurationInfo
                      className={classes.duration}
                      durationName={hit?.user || ''}
                      duration={formatDateToNewsFormat(hit?.published_at)}
                    />
                  }
                  icon={<StyledIdeaIcon />}
                  onClick={handleHitClick(hit)}
                />
              ))}
            </Subsection>
          </>
        )}
      </Content>
    </Wrapper>
  );
};

export default Dropdown;

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  position: fixed;
  top: 80px;
  left: 0;
  z-index: -1;
  display: flex;
  width: 100%;
  max-height: calc(100vh - 50px);
  padding: ${SMALL_MARGIN_PX} 6px ${SUBMIDDLE_MARGIN_PX} 16px;
  box-sizing: border-box;
  border-radius: 0 0 ${MIDDLE_RADIUS_PX} ${MIDDLE_RADIUS_PX};
  background: ${colorsPalette.blackSecondary};
  box-shadow: 0 3px 6px ${alpha(colorsPalette.black, 0.4)};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    position: absolute;
    top: 0;
    padding: ${LARGE_MARGIN_PX} 6px ${SUBMIDDLE_MARGIN_PX} 16px;
    border-radius: 27px 27px ${MIDDLE_RADIUS_PX} ${MIDDLE_RADIUS_PX};
  }

  ${({ optionalStyles }) => optionalStyles}
`;
const Content = styled('div')`
  width: 100%;
  padding-right: 12px;
  overflow-y: auto;
  transition: height 0.3s;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${colorsPalette.blueDark};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background: ${alpha(colorsPalette.blue, 0.33)};
  }
`;
const SectionTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 14px;

  &:first-letter {
    text-transform: uppercase;
  }
`;
const SubsectionTitle = styled(Typography)`
  padding-bottom: 4px;
  margin-bottom: ${SMALL_MARGIN_PX};
  color: ${alpha(colorsPalette.white, 0.33)};
  border-bottom: 1px solid ${alpha(colorsPalette.white, 0.33)};
  font-size: 14px;

  &:first-letter {
    text-transform: uppercase;
  }
`;
const Subsection = styled('div')`
  display: flex;
  flex-direction: column;
`;
const ItemTitle = styled(Typography)`
  font-weight: 700;
  line-height: 18px;
  margin-bottom: 6px;

  & mark {
    color: ${colorsPalette.white};
    background: ${colorsPalette.blue};
  }
`;
const Subcategory = styled(Typography)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${SMALL_MARGIN_PX} 0;
  margin-bottom: ${SMALL_MARGIN_PX};
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: ${colorsPalette.gray};
  cursor: pointer;

  & mark {
    color: ${colorsPalette.white};
    background: ${colorsPalette.blue};
  }
`;
const ItemWrapper = styled(Link)`
  display: flex;
  margin-bottom: ${SMALL_MARGIN_PX};
  cursor: pointer;
  color: inherit;
`;
const SelectedCategories = styled('div')`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 12px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-top: ${TINY_MARGIN_PX};
  }
`;
const SelectedCategory = styled('span')`
  display: flex;
  align-items: center;
  margin-right: ${TINY_MARGIN_PX};
  margin-bottom: ${TINY_MARGIN_PX};
  padding: 3px 14px;
  border-radius: 5px;
  font-weight: 700;
  font-size: 14px;
  color: ${colorsPalette.violetSecondary};
  background: ${colorsPalette.violetAnalyticsCard};
  cursor: pointer;
`;
const StyledFilterIcon = styled(FilterIcon)`
  width: 16px;

  & path {
    fill: ${colorsPalette.gray};
  }
`;
const StyledCloseIcon = styled(CloseIcon)`
  width: 8px;
  height: 8px;
  margin-left: ${TINY_MARGIN_PX};
  color: ${colorsPalette.violetSecondary};
`;
const positionIcon = (Icon) => styled(Icon)`
  width: 24px;
  height: 24px;
  margin-right: ${SMALL_MARGIN_PX};
`;
const StyledVideoIcon = styled(positionIcon(VideoIcon))`
  color: ${colorsPalette.violet};

  & path {
    stroke: ${colorsPalette.violet};
  }
`;
const StyledPathwayIcon = positionIcon(PathwayIcon);
const StyledIdeaIcon = positionIcon(Idea);
const StyledOpenedBook = positionIcon(OpenedBook);
